<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  score: {
    type: Number,
    default: 0,
  },
  grade: {
    type: String,
    default: 'F',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Performance Score',
  },
});

const scoreColor = computed(() => {
  if (props.score >= 90) return 'text-n-teal-11';
  if (props.score >= 80) return 'text-n-blue-11';
  if (props.score >= 70) return 'text-n-amber-11';
  if (props.score >= 60) return 'text-n-orange-11';
  return 'text-n-ruby-11';
});

const gradeColor = computed(() => {
  const colors = {
    A: 'bg-n-teal-9 text-white',
    B: 'bg-n-blue-9 text-white',
    C: 'bg-n-amber-9 text-white',
    D: 'bg-n-orange-9 text-white',
    F: 'bg-n-ruby-9 text-white',
  };
  return colors[props.grade] || colors.F;
});

const circumference = 2 * Math.PI * 45;
const strokeDasharray = computed(() => {
  const progress = (props.score / 100) * circumference;
  return `${progress} ${circumference}`;
});
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-6">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-gauge text-lg text-n-blue-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col items-center gap-4">
      <!-- Circular Progress -->
      <div class="relative">
        <svg class="w-40 h-40 transform -rotate-90">
          <circle
            cx="80"
            cy="80"
            r="45"
            stroke-width="10"
            fill="none"
            class="stroke-n-alpha-2"
          />
          <circle
            cx="80"
            cy="80"
            r="45"
            stroke-width="10"
            fill="none"
            stroke-linecap="round"
            :class="scoreColor.replace('text-', 'stroke-').replace('-11', '-9')"
            :stroke-dasharray="strokeDasharray"
            style="transition: stroke-dasharray 0.5s ease"
          />
        </svg>
        <div class="absolute inset-0 flex flex-col items-center justify-center">
          <span class="text-4xl font-bold" :class="scoreColor">
            {{ score.toFixed(0) }}
          </span>
          <span class="text-sm text-n-slate-11">de 100</span>
        </div>
      </div>

      <!-- Grade Badge -->
      <div class="flex items-center gap-3">
        <span class="text-sm text-n-slate-11">Nota:</span>
        <span
          class="px-4 py-2 rounded-lg text-2xl font-bold"
          :class="gradeColor"
        >
          {{ grade }}
        </span>
      </div>

      <!-- Legend -->
      <div class="flex flex-wrap justify-center gap-3 mt-2 text-xs">
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded-full bg-n-teal-9" />
          <span class="text-n-slate-11">90+ Excelente</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded-full bg-n-blue-9" />
          <span class="text-n-slate-11">80+ Bom</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded-full bg-n-amber-9" />
          <span class="text-n-slate-11">70+ Regular</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded-full bg-n-ruby-9" />
          <span class="text-n-slate-11">&lt;70 Precisa Melhorar</span>
        </div>
      </div>
    </div>
  </div>
</template>
